import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/supabase'

interface User {
  id: string
  email?: string
  [key: string]: any
}

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
}

// 根据错误类型返回相应的提示信息
const getErrorMessage = (error: any, t: any) => {
  const errorMessage = error?.message || ''
  const errorCode = error?.code || ''

  // 注册时邮箱已存在
  if (errorMessage.includes('user_already_exists') ||
    errorMessage.includes('User already registered') ||
    errorMessage.includes('A user with this email address has already been registered') ||
    errorCode === 'user_already_exists') {
    return t('error.emailAlreadyRegistered')
  }

  // 用户不存在（明确的错误信息）
  if (errorMessage.includes('User not found') ||
    errorMessage.includes('user_not_found') ||
    errorCode === 'user_not_found') {
    return t('error.emailNotFound')
  }

  // 邮箱验证相关
  if (errorMessage.includes('Email not confirmed') ||
    errorMessage.includes('Email not verified') ||
    errorCode === 'email_not_confirmed') {
    return t('error.emailNotVerified')
  }

  // 频率限制
  if (errorMessage.includes('Too many requests') ||
    errorMessage.includes('over_request_rate_limit') ||
    errorCode === 'over_request_rate_limit') {
    return t('error.tooManyRequests')
  }

  // 网络错误
  if (errorMessage.includes('Network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('ERR_NETWORK')) {
    return t('error.networkError')
  }

  // 弱密码错误
  if (errorMessage.includes('Password should be at least') ||
    errorMessage.includes('Password is too short') ||
    errorCode === 'weak_password') {
    return t('error.passwordMinLength')
  }

  // 明确的密码错误
  if (errorMessage.includes('Invalid password') ||
    errorMessage.includes('password is incorrect') ||
    errorCode === 'invalid_password') {
    return t('error.passwordIncorrect')
  }

  // 对于 "Invalid login credentials" 这种通用错误，根据上下文和错误代码进行更精确的判断
  if (errorMessage.includes('Invalid login credentials') ||
    errorMessage.includes('invalid_credentials')) {

    // 如果有更具体的错误代码，根据代码判断
    if (errorCode === 'invalid_credentials') {
      // 这种情况下，为了安全考虑，不明确说明是邮箱还是密码错误
      // 但可以给一个更友好的提示
      return t('error.invalidCredentials')
    }

    // 对于登录场景，当邮箱不存在时，Supabase 通常也会返回 "Invalid login credentials"
    // 为了避免信息泄露，我们可以保持这种模糊的提示，但提供更好的用户体验
    return t('error.invalidCredentials')
  }

  // 默认返回通用错误提示
  return t('error.unknownError')
}

export const useAuth = () => {
  const { t } = useTranslation('auth')
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })

  // 密码重置状态
  const [resetPasswordState, setResetPasswordState] = useState({
    loading: false,
    success: false,
    error: null,
  })

  // 修改密码状态
  const [changePasswordState, setChangePasswordState] = useState({
    loading: false,
    success: false,
    error: null,
  })

  useEffect(() => {
    // 检查当前用户
    const checkUser = async () => {
      try {
        const { user, error } = await authService.getCurrentUser()
        if (error) {
          // 如果是认证相关的错误，不显示为错误，只是设置为未登录状态
          if (error.message.includes('Auth session missing') || error.message.includes('Invalid token')) {
            setAuthState(prev => ({ ...prev, user: null, loading: false }))
          } else {
            setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
          }
        } else {
          setAuthState(prev => ({ ...prev, user, loading: false }))
        }
      } catch (err) {
        console.error("Auth initialization error:", err)
        // 发生错误时（如 refresh_token 失效），强制清除本地 session 以免死循环
        await authService.signOut()

        setAuthState(prev => ({
          ...prev,
          loading: false,
          user: null
        }))
      }
    }

    checkUser()

    // 监听认证状态变化
    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setAuthState(prev => ({ ...prev, user: session.user, loading: false }))
        } else if (event === 'SIGNED_OUT') {
          setAuthState(prev => ({ ...prev, user: null, loading: false }))
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setAuthState(prev => ({ ...prev, user: session.user, loading: false }))
        }
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const { data, error } = await authService.signIn(email, password)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      setAuthState(prev => ({
        ...prev,
        user: data.user,
        loading: false
      }))

      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  const signUp = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const { data, error } = await authService.signUp(email, password)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      // 注册成功时也要设置 loading 为 false
      setAuthState(prev => ({
        ...prev,
        loading: false,
        user: data.user
      }))

      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  const signOut = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const { error } = await authService.signOut()

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      setAuthState(prev => ({
        ...prev,
        user: null,
        loading: false
      }))

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // 重置密码功能
  const resetPassword = async (email: string) => {
    setResetPasswordState(prev => ({ ...prev, loading: true, error: null, success: false }))

    try {
      const { error } = await authService.resetPassword(email)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setResetPasswordState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      setResetPasswordState(prev => ({
        ...prev,
        loading: false,
        success: true
      }))

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setResetPasswordState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // 更新密码功能
  const updatePassword = async (newPassword: string) => {
    setChangePasswordState(prev => ({ ...prev, loading: true, error: null, success: false }))

    try {
      const { error } = await authService.updatePassword(newPassword)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setChangePasswordState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      setChangePasswordState(prev => ({
        ...prev,
        loading: false,
        success: true
      }))

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setChangePasswordState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // 使用令牌重置密码
  const resetPasswordWithToken = async (newPassword: string) => {
    setResetPasswordState(prev => ({ ...prev, loading: true, error: null, success: false }))

    try {
      const { error } = await authService.resetPasswordWithToken(newPassword)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setResetPasswordState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      setResetPasswordState(prev => ({
        ...prev,
        loading: false,
        success: true
      }))

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setResetPasswordState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Google OAuth 登录
  const signInWithGoogle = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const { error } = await authService.signInWithGoogle()

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      // OAuth 会重定向到 Google，不需要手动更新状态
      // 用户授权后会重定向回来，由 onAuthStateChange 处理
      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Google ID Token 登录 (用于 One Tap)
  const signInWithGoogleIdToken = async (idToken: string, nonce?: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const { data, error } = await authService.signInWithGoogleIdToken(idToken, nonce)

      if (error) {
        const userFriendlyError = getErrorMessage(error, t)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: userFriendlyError
        }))
        return { success: false, error: userFriendlyError }
      }

      // 登录成功，状态会由 onAuthStateChange 自动更新
      setAuthState(prev => ({
        ...prev,
        user: data.user,
        loading: false
      }))

      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t)
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // 清除错误状态
  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }))
    setResetPasswordState(prev => ({ ...prev, error: null, success: false }))
    setChangePasswordState(prev => ({ ...prev, error: null, success: false }))
  }

  // 清除密码重置状态
  const clearResetPasswordState = () => {
    setResetPasswordState({
      loading: false,
      success: false,
      error: null,
    })
  }

  // 清除修改密码状态
  const clearChangePasswordState = () => {
    setChangePasswordState({
      loading: false,
      success: false,
      error: null,
    })
  }

  return {
    ...authState,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithGoogleIdToken,
    resetPassword,
    updatePassword,
    resetPasswordWithToken,
    resetPasswordState,
    changePasswordState,
    clearError,
    clearResetPasswordState,
    clearChangePasswordState,
  }
}